/**
 * SmartSection Component - Simplified Implementation
 *
 * A simple, easy-to-use collapsible section component for DialogContent.
 * Focused on ease of use, minimal configuration, and visual clarity.
 */

import React, { useState, useContext, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { HelpTooltip } from '../HelpTooltip';
import { FieldUpdateContext } from '../../contexts/FieldUpdateContext';
import { FieldUpdateContextValue } from '../../types';
import { SectionUpdateIndicator } from '../FieldUpdateIndicator';

// ============================================================================
// TYPES
// ============================================================================

export interface SmartSectionProps {
  /**
   * Section title (required)
   */
  title: string;

  /**
   * Optional subtitle
   */
  subtitle?: string;

  /**
   * Section content
   */
  children: React.ReactNode;

  /**
   * Whether the section can be collapsed
   * @default false
   */
  collapsible?: boolean;

  /**
   * Initial expanded state (when collapsible)
   * @default true
   */
  defaultExpanded?: boolean;

  /**
   * Initial collapsed state (when collapsible) - alternative to defaultExpanded
   * @default false
   */
  defaultCollapsed?: boolean;

  /**
   * Optional icon to display before title
   */
  icon?: React.ReactNode;

  /**
   * Help content ID for help system integration
   */
  helpId?: string;

  /**
   * Static help content (overrides helpId)
   */
  helpContent?: string;

  /**
   * Callback when help is clicked
   */
  onHelpClick?: (helpId?: string) => void;

  /**
   * Callback when section is toggled (only for collapsible sections)
   */
  onToggle?: (expanded: boolean) => void;

  /**
   * Alternative callback prop name for consistency with other components
   */
  onToggleCollapse?: (collapsed: boolean) => void;

  /**
   * Field path for update tracking (dot notation, e.g., 'screens.0' or 'basicInfo')
   */
  trackUpdatesFor?: string;

  /**
   * Show update indicators for this section
   * @default true when trackUpdatesFor is provided
   */
  showUpdateIndicators?: boolean;

  /**
   * Auto-expand section when updates are detected
   * @default false
   */
  expandOnUpdates?: boolean;

  /**
   * Paper variant
   * @default 'outlined'
   */
  variant?: 'elevation' | 'outlined';

  /**
   * Section size
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Whether the section is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Custom styles
   */
  sx?: SxProps<Theme>;

  /**
   * Test ID for testing
   */
  'data-testid'?: string;

  /**
   * CSS class name
   */
  className?: string;
}

// ============================================================================
// NESTING PREVENTION
// ============================================================================

// Context to track SmartSection nesting
const SmartSectionContext = React.createContext<boolean>(false);

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * SmartSection - A simple, theme-aware collapsible section
 *
 * Usage:
 * ```tsx
 * <SmartSection title="User Information">
 *   <TextField label="Name" />
 *   <TextField label="Email" />
 * </SmartSection>
 *
 * <SmartSection
 *   title="Advanced Settings"
 *   collapsible
 *   defaultExpanded={false}
 *   icon={<SettingsIcon />}
 * >
 *   <TextField label="API Key" />
 * </SmartSection>
 * ```
 */
export const SmartSection: React.FC<SmartSectionProps> = ({
  title,
  subtitle,
  children,
  collapsible = false,
  defaultExpanded = true,
  defaultCollapsed,
  icon,
  helpId,
  helpContent,
  onHelpClick,
  onToggle,
  onToggleCollapse,
  trackUpdatesFor,
  showUpdateIndicators,
  expandOnUpdates = false,
  variant = 'outlined',
  size = 'medium',
  disabled = false,
  sx,
  'data-testid': dataTestId,
  className,
}) => {
  const theme = useTheme();
  const fieldUpdateContext = useContext(FieldUpdateContext) as FieldUpdateContextValue | null;
  const isNestedSmartSection = useContext(SmartSectionContext);

  // Prevent nesting of SmartSection components
  if (isNestedSmartSection) {
    throw new Error('SmartSection components cannot contain other SmartSection components');
  }

  // Prioritize defaultCollapsed if provided, otherwise use defaultExpanded
  const initialExpanded = defaultCollapsed !== undefined ? !defaultCollapsed : defaultExpanded;
  const [expanded, setExpanded] = useState(collapsible ? initialExpanded : true);

  // Check for recent updates in this section
  const sectionHasUpdates = useMemo(() => {
    if (!fieldUpdateContext || !trackUpdatesFor) return false;

    const now = Date.now();
    const oneMinuteAgo = now - (60 * 1000);

    return fieldUpdateContext.fieldUpdates.some(update =>
      update.path.startsWith(trackUpdatesFor) &&
      update.timestamp > oneMinuteAgo &&
      update.source !== 'user' // Don't show indicators for user's own changes
    );
  }, [fieldUpdateContext?.fieldUpdates, trackUpdatesFor]);

  // Auto-expand on updates
  useEffect(() => {
    if (sectionHasUpdates && expandOnUpdates && collapsible && !expanded) {
      setExpanded(true);
      onToggle?.(true);
      onToggleCollapse?.(false);
    }
  }, [sectionHasUpdates, expandOnUpdates, collapsible, expanded, onToggle, onToggleCollapse]);

  // Determine if we should show update indicators
  const shouldShowIndicators = showUpdateIndicators ?? (trackUpdatesFor !== undefined);
  const hasVisibleUpdates = shouldShowIndicators && sectionHasUpdates;

  const handleToggle = () => {
    if (!collapsible || disabled) return;

    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onToggle?.(newExpanded);
    onToggleCollapse?.(!newExpanded); // Opposite for collapsed callback
  };

  return (
    <SmartSectionContext.Provider value={true}>
      <Paper
        variant={variant}
        className={className}
        data-testid={dataTestId || 'smart-section'}
        sx={{
          borderRadius: theme.shape.borderRadius,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          ...sx,
        }}
      >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
          padding: theme.spacing(2),
          backgroundColor: hasVisibleUpdates
            ? alpha(theme.palette.info.main, 0.08)
            : alpha(theme.palette.action.hover, 0.04),
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderLeft: hasVisibleUpdates ? `3px solid ${theme.palette.info.main}` : 'none',
          cursor: collapsible ? 'pointer' : 'default',
          '&:hover': collapsible ? {
            backgroundColor: hasVisibleUpdates
              ? alpha(theme.palette.info.main, 0.12)
              : alpha(theme.palette.action.hover, 0.08),
          } : {},
          transition: theme.transitions.create(['background-color', 'border-left-color'], {
            duration: theme.transitions.duration.short,
          }),
        }}
        onClick={handleToggle}
      >
        {/* Icon */}
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: theme.palette.text.primary,
            }}
          >
            {icon}
          </Box>
        )}

        {/* Title and Subtitle */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="h6"
              component="h6"
              sx={{
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.text.primary,
                margin: 0,
                lineHeight: 1.2,
              }}
              noWrap
            >
              {title}
            </Typography>
            {hasVisibleUpdates && trackUpdatesFor && (
              <SectionUpdateIndicator
                sectionPath={trackUpdatesFor}
                maxIndicators={2}
                showCount={true}
              />
            )}
          </Box>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                marginTop: theme.spacing(0.25),
                lineHeight: 1.3,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Help Icon */}
        {(helpId || helpContent) && (
          <HelpTooltip
            helpId={helpId || ''}
            content={helpContent}
            size="small"
            placement="top"
            onClick={onHelpClick}
            data-testid="smart-section-help"
            sx={{
              marginRight: collapsible ? theme.spacing(0.5) : 0,
            }}
          />
        )}

        {/* Collapse Toggle */}
        {collapsible && (
          <IconButton
            size="small"
            disabled={disabled}
            data-testid="section-toggle"
            aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
            aria-expanded={expanded}
            sx={{
              color: theme.palette.action.active,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: theme.transitions.create(['transform', 'color'], {
                duration: theme.transitions.duration.short,
              }),
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.primary.main,
              },
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent double toggle from Box click
              handleToggle();
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </Box>

      {/* Content */}
      <Collapse
        in={!collapsible || expanded}
        timeout={theme.transitions.duration.standard}
        easing={theme.transitions.easing.easeInOut}
      >
        <Box
          sx={{
            padding: theme.spacing(2),
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {children}
        </Box>
      </Collapse>
      </Paper>
    </SmartSectionContext.Provider>
  );
};

SmartSection.displayName = 'SmartSection';

export default SmartSection;